import { useEffect, useMemo, useState } from "react";

interface StoreState<T> {
  createdAt: number;
  data: T;
}

export const useValueStore = <T>(
  store: Storage,
  key: string,
  maxAge: number = 5 * 60 * 1000,
) => {
  const [state, setState] = useState<StoreState<T> | null>(loadFromStore);

  const expiry = (state?.createdAt || 0) + maxAge;

  useEffect(() => {
    if (state === null) {
      store.removeItem(key);
    } else {
      store.setItem(key, JSON.stringify(state, replacer));
    }
  }, [state]);

  useEffect(() => {
    if (!state) {
      return;
    }

    if (expiry <= Date.now()) {
      setState(null);
      return;
    }

    const timeout = setTimeout(() => setState(null), expiry - Date.now());
    return () => clearTimeout(timeout);
  }, [state]);

  return useMemo(
    () => ({
      data: state?.data ?? null,
      createdAt: state?.createdAt ?? null,
      expiry,
      get age() {
        if (state && !this.hasExpired) {
          return Date.now() - state.createdAt;
        }
        return null;
      },
      get hasExpired() {
        return expiry < Date.now();
      },
      setValue(data: T | null) {
        if (data === null) {
          setState(null);
        } else {
          const newData = {
            data,
            createdAt: Date.now(),
          };
          setState(newData);
        }
      },
    }),
    [state],
  );

  function loadFromStore() {
    const item = store.getItem(key);

    if (item === null) {
      return item;
    }

    return JSON.parse(item, reviver);
  }
};

function reviver(key: string, value: unknown) {
  if (typeof value === "string" && /^[0-9]+n$/.test(value)) {
    return BigInt(value.slice(0, -1));
  }
  return value;
}

function replacer(key: string, value: unknown) {
  if (typeof value === "bigint") {
    return value.toString() + "n";
  }
  return value;
}
